/**
 * 69.23 (DECISIONS 241, the owner: "it would force bot behavior to be somewhat specialized in the mobile indirect fire
 * support role"): a bot squad leader calls its side's mortar — the crew never fires on its own (core/mortar.ts). On its look,
 * with a mortar of its side set, a leader on foot calls on the enemy it has seen with the most others it saw round it
 * (MORTAR_CALL.clusterPeople; the nearer on a tie), at least MORTAR_CALL.minPeople from itself and with none of its side
 * within the strike's reach of the point (the bots never call near their own, as with the airstrike). The Match answers the
 * call (its intent's `call`) or refuses it quietly — out of reach, or the mortar already firing a mission.
 * 69.42 (d) (DECISIONS 253 (2), with GameConfig.munitionsSupply): the calls paced to the bombs — the fewer the side's best
 * stocked mortar holds, the more enemies the call wants together (MORTAR_CALL.pace: its share of the store → the cluster), and
 * none with the bombs out; the last missions kept for a group, not spent on one rifleman.
 * 69.42 (f): only on enemies holding still — each seen at this look and the one before, moved under MORTAR_CALL.stillPeople a
 * second between them (a crawl is faster): the bombs land seconds after the call (the lay, the stagger, the flight), and
 * on Foothold four in five landed where one enemy or none still stood, the group called on gone. A group in place — at a
 * point, in cover, firing — is still there.
 */
import type { GameConfig, GameState, PlayerState, StructureState, Vec3 } from '../types';
import { brainFace } from '../core/brainFace';
import { arcDistance } from '../core/gameState';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { friendliesInStrikeReach } from '../core/airstrike';
import { mortarLays, mortarSet, mortarsOf } from '../core/mortar';
import { teamPicture } from '../core/picture';
import { MORTAR, PHYS_DT } from '../constants';

/** A leader's call: not on an enemy nearer itself than `minPeople` (danger close), the cluster counted within `clusterPeople` of it. */
export const MORTAR_CALL = { minPeople: 30, clusterPeople: 10, stillPeople: 0.8, pace: [{ share: 2 / 3, cluster: 1 }, { share: 1 / 3, cluster: 2 }, { share: 0, cluster: 3 }] as const };

/**
 * 69.42 (d): the enemies the call wants together on `planetIndex` — 1 without the munitions switch, else by the bombs of the
 * side's best stocked mortar there (MORTAR_CALL.pace); null with none left (or no mortar).
 */
export function mortarCluster(state: GameState, team: number, planetIndex: number): number | null {
  if (!state.config.munitionsSupply) return 1;
  const cap = state.config.mortarStore ?? MORTAR.store;
  const most = Math.max(0, ...mortarsOf(state, team).filter(s => s.planetIndex === planetIndex).map(s => s.mortar?.rounds ?? 0));
  if (most <= 0 || cap <= 0) return null;
  return MORTAR_CALL.pace.find(p => most >= p.share * cap)!.cluster;
}

/** Whether `unit` leads its squad (its first living member). */
function leads(state: GameState, unit: PlayerState): boolean {
  const sq = (state.squads ?? []).find(q => q.unitIds.includes(unit.id));
  return !!sq && sq.unitIds.find(id => state.players.some(p => p.id === id && p.alive)) === unit.id;
}

/** The point `unit` (a bot squad leader on foot, on its look, with a mortar of its side set) calls the mortar on, or null. */
export function mortarCallFor(state: GameState, unit: PlayerState): { planetIndex: number; surfaceDir: Vec3 } | null {
  if (!unit.alive || unit.mount || !leads(state, unit)) return null;
  const face = brainFace(state, unit.id);
  const person0 = PERSON_BODY_HEIGHT * personSetOf(state).k, R0 = state.planets[unit.planetIndex]?.radius ?? 1;
  // 69.42 (f): the ones holding still since the look before (none before: none to call on)
  const still = (x: { id: number; surfaceDir: Vec3 }) => {
    const was = face?.seenBefore?.find(y => y.id === x.id), dt = was && face?.seenTick !== undefined ? (face.seenTick - was.tick) * PHYS_DT : 0;
    return !!was && dt > 0 && arcDistance(was.surfaceDir, x.surfaceDir, R0) <= MORTAR_CALL.stillPeople * person0 * dt;
  };
  const seen = (face?.seenAt ?? []).filter(x => x.planetIndex === unit.planetIndex && still(x));
  if (!seen.length) return null;
  const want = mortarCluster(state, unit.teamIndex, unit.planetIndex);   // (69.42 (d): paced to the bombs)
  if (want === null || seen.length < want) return null;
  const planet = state.planets[unit.planetIndex];
  if (!planet) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, R = planet.radius;
  let best: { at: Vec3; n: number; d: number } | null = null;
  for (const e of seen) {
    const d = arcDistance(unit.surfaceDir, e.surfaceDir, R);
    if (d < MORTAR_CALL.minPeople * person) continue;
    const n = seen.filter(o => arcDistance(o.surfaceDir, e.surfaceDir, R) <= MORTAR_CALL.clusterPeople * person).length;
    if (!best || n > best.n || (n === best.n && d < best.d)) best = { at: e.surfaceDir, n, d };
  }
  if (!best || best.n < want || friendliesInStrikeReach(state, unit.teamIndex, unit.planetIndex, best.at) > 0) return null;
  // (BUILDLIST 69.23, the refused calls: only where a tube of its side lays — callMortar's test — or a leader calls on every
  // look into a tube that cannot answer, 5,171 refusals in one fhTug25m game. The best cluster's or none: a fall to the next
  // that lays makes missions master never fired — fhTug25m fh-4 on 225, 2 of 9 calls — so it would take a version)
  const at = best.at;
  if (!mortarsOf(state, unit.teamIndex).some(t => t.planetIndex === unit.planetIndex && mortarLays(state, t, at))) return null;
  return { planetIndex: unit.planetIndex, surfaceDir: { ...best.at } };
}

/**
 * 69.23's own fire (DECISIONS 438 (3), the design note approved Nov 3; GameConfig.mortarOwnFire, per team, off): a set tube
 * that no leader calls fires on what its side knows. On its look the tube's crewman makes the call itself — the same call,
 * the Match's (core/mortar.ts callMortar) — from the side's picture, never the truth: identified tracks at most
 * `freshSeconds` old (the bombs land seconds after), each still — two fixes at least `stillSeconds` apart, moved under
 * MORTAR_CALL.stillPeople a second between them (69.42 (f), the leader's rule) — at least MORTAR_CALL.minPeople from the
 * tube, on ground it can lay on, and a group of one more than the pace's cluster, at least 2 (nobody's eye confirms it),
 * none of its side within the strike's reach. The commander's calls first: never within `quietSeconds` of any mission of
 * the tube's or while one fires (mortarSet), at most one own mission every `everySeconds`, and only above `reserveShare` of
 * the store — the last bombs kept for the leaders' calls (with or without munitionsSupply: without it nothing refills).
 */
export const MORTAR_OWN = { quietSeconds: 20, everySeconds: 30, freshSeconds: 6, stillSeconds: 1, reserveShare: 0.5 };

/** The crewman's memory for its own fire (its brain's `ownFire`): each track's first fix of its stay, the tube's last mission called, its own last call. */
export interface OwnFireMemory { fixes: Array<{ id: number; surfaceDir: Vec3; tick: number }>; calledAt?: number; ownAt?: number }

/** Whether `team`'s mortars fire on their own (GameConfig.mortarOwnFire: off unless true). */
export function mortarOwnFireOn(config: Pick<GameConfig, 'mortarOwnFire'>, team: number): boolean {
  const f = config.mortarOwnFire;
  return Array.isArray(f) ? f[team] === true : f === true;
}

/**
 * On its look, the crewman at `tube`'s tube: its memory brought up to date (the picture's fixes, the tube's missions) and
 * the point it calls on, or null.
 */
export function mortarOwnCallFor(state: GameState, unit: PlayerState, tube: StructureState, tick: number, was: OwnFireMemory | undefined): { call: { planetIndex: number; surfaceDir: Vec3 } | null; mem: OwnFireMemory } {
  const team = unit.teamIndex, pi = tube.planetIndex, planet = state.planets[pi];
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, R = planet?.radius ?? 1;
  // the tube's missions as they fly (the latest call it answered: a leader's or its own)
  let calledAt = was?.calledAt;
  for (const x of state.airstrikes?.inbound ?? []) {
    if (x.kind === 'mortar' && (x.laying?.mortar?.gun === tube.id || x.rounds.some(r => r.gun === tube.id)) && (calledAt === undefined || x.calledTick > calledAt)) calledAt = x.calledTick;
  }
  // the picture's identified enemies on its planet, each with the first fix of its stay (a move past the still pace starts a new one)
  const known = teamPicture(state, team, tick).filter(k => k.unitId !== undefined && k.unitTeam !== team && k.planetIndex === pi);
  const fixes: OwnFireMemory['fixes'] = [];
  const still: Vec3[] = [];
  for (const k of known) {
    const f = was?.fixes.find(x => x.id === k.unitId);
    const dt = f ? (k.tick - f.tick) * PHYS_DT : 0;
    const stays = !!f && dt >= 0 && arcDistance(f.surfaceDir, k.surfaceDir, R) <= MORTAR_CALL.stillPeople * person * Math.max(dt, PHYS_DT);
    const first = stays ? f! : { id: k.unitId!, surfaceDir: { ...k.surfaceDir }, tick: k.tick };
    fixes.push(first);
    if ((k.tick - first.tick) * PHYS_DT >= MORTAR_OWN.stillSeconds && (tick - k.tick) * PHYS_DT <= MORTAR_OWN.freshSeconds) still.push(k.surfaceDir);
  }
  const mem: OwnFireMemory = { fixes, ...(calledAt !== undefined ? { calledAt } : {}), ...(was?.ownAt !== undefined ? { ownAt: was.ownAt } : {}) };
  const none = { call: null, mem };
  // the commander's calls first: the tube set and quiet, its own pace, the reserve kept
  if (!planet || !still.length || !mortarSet(state, tube, tick)) return none;
  if (calledAt !== undefined && (tick - calledAt) * PHYS_DT < MORTAR_OWN.quietSeconds) return none;
  if (mem.ownAt !== undefined && (tick - mem.ownAt) * PHYS_DT < MORTAR_OWN.everySeconds) return none;
  if (tube.mortar!.rounds <= MORTAR_OWN.reserveShare * (state.config.mortarStore ?? MORTAR.store)) return none;
  const pace = mortarCluster(state, team, pi);
  if (pace === null) return none;
  const want = Math.max(2, pace + 1);
  if (still.length < want) return none;
  let best: { at: Vec3; n: number; d: number } | null = null;
  for (const e of still) {
    const d = arcDistance(tube.surfaceDir, e, R);
    if (d < MORTAR_CALL.minPeople * person || !mortarLays(state, tube, e)) continue;
    const n = still.filter(o => arcDistance(o, e, R) <= MORTAR_CALL.clusterPeople * person).length;
    if (!best || n > best.n || (n === best.n && d < best.d)) best = { at: e, n, d };
  }
  if (!best || best.n < want || friendliesInStrikeReach(state, team, pi, best.at) > 0) return none;
  return { call: { planetIndex: pi, surfaceDir: { ...best.at } }, mem: { ...mem, ownAt: tick } };
}
